import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Users, Video, Camera, Scissors, Sparkles,
  Search, BrainCircuit, SlidersHorizontal, Handshake, ShieldCheck, Target,
} from "lucide-react";
import { OrbitVisual } from "../components/OrbitVisual";
import { Section, SectionHeading, Eyebrow, GlassCard, Reveal } from "../components/ui-shared";

const scrollToInvite = () => {
  const el = document.getElementById("enter-orbit");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const InviteButton = ({ className = "", testid = "cta-request-invite" }) => (
  <button onClick={scrollToInvite} data-testid={testid} className={`btn-primary ${className}`}>
    Request an Invite <ArrowRight size={18} />
  </button>
);

const differentiators = [
  { t: "DISCOVER", d: "Find campaign-relevant talent without endless searching.", icon: Search },
  { t: "INTELLIGENCE", d: "Turn campaign requirements and available performance information into a more informed discovery process.", icon: BrainCircuit },
  { t: "CONTROL", d: "Keep communication, collaboration and decisions directly in your hands.", icon: SlidersHorizontal },
  { t: "FLEXIBILITY", d: "Paid campaigns, barter collaborations and mutually agreed arrangements — collaborate on your terms.", icon: Handshake },
];

const talent = [
  { t: "CREATORS", icon: Users },
  { t: "VIDEOGRAPHERS", icon: Video },
  { t: "PHOTOGRAPHERS", icon: Camera },
  { t: "EDITORS", icon: Scissors },
  { t: "SPECIALIST TALENT", icon: Sparkles },
];

export default function Home() {
  return (
    <div>
      {/* HERO — above the fold */}
      <section className="relative pt-12 sm:pt-16 lg:pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <span className="pill" data-testid="hero-badge-private-beta">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
                  Private Beta • Now Live
                </span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.08 }}
                data-testid="hero-headline"
                className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-white"
              >
                YOUR NEXT HIRE.<br />
                <span className="bg-gradient-to-r from-sky-300 via-blue-400 to-purple-400 bg-clip-text text-transparent drop-shadow-[0_0_28px_rgba(59,130,246,0.45)]">
                  5 MINUTES AWAY.
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.14 }}
                className="mt-5 text-base sm:text-lg text-white leading-relaxed max-w-xl font-semibold"
              >
                STOP CHASING, START ORBITING.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.16 }}
                className="mt-4 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl font-medium"
              >
                Orbit Intelligence makes discovering the right talent for your campaigns faster, smarter and far more direct.
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.24 }}
                className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl"
              >
                Go beyond creator discovery. Find creators, videographers, photographers, editors and other campaign-relevant
                talent through one intelligent discovery layer — built around your brief, budget and performance signals, without
                endless searching, scattered DMs or unnecessary middlemen.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.32 }}
                className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-xl"
              >
                {["YOUR CAMPAIGN.", "YOUR BUDGET.", "YOUR TERMS.", "JUST A FEW CLICKS."].map((t) => (
                  <div key={t} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-center sm:text-left">
                    <span className="font-display font-semibold text-xs sm:text-[0.8rem] text-white leading-tight">{t}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-7"
              >
                <InviteButton testid="hero-cta-invite" />
                <p className="mt-3 font-mono text-xs text-slate-500">Private access • 0% platform fee • Risk-free participation</p>
              </motion.div>
            </div>

            <div className="relative">
              <OrbitVisual />
            </div>
          </div>
        </div>
      </section>

      {/* EXCLUSIVE PRIVATE BETA */}
      <Section className="!pt-6 !pb-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl glass p-6 sm:p-10">
            <div className="absolute inset-0 -z-10 opacity-80"
              style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(37,99,235,0.22), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(14,165,233,0.16), transparent 55%)" }} />
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <Eyebrow>Exclusive Private Beta</Eyebrow>
                <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed max-w-xl">
                  We're currently onboarding a select group of businesses across markets into Neo Orbit's private beta —
                  giving early participants an exclusive opportunity to experience the app before wider access.
                </p>
              </div>
              <div className="lg:text-right">
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
                  0% PLATFORM FEE <span className="text-sky-300">DURING BETA.</span>
                </h2>
                <p className="mt-3 text-sm text-slate-400 lg:ml-auto max-w-md">
                  If Orbit doesn't work for your campaign, you don't pay us anything.
                </p>
                <div className="mt-5 lg:flex lg:justify-end">
                  <InviteButton testid="beta-top-cta-invite" />
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* NOT ANOTHER REGULAR PLATFORM */}
      <Section className="!pt-4">
        <Reveal>
          <SectionHeading
            eyebrow="Orbit Intelligence"
            title="A SUPER INTELLIGENT APP FOR INTELLIGENT MARKETING."
            subtitle="Neo Orbit is designed to remove the friction sitting between a business and the people it needs for its work."
          />
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {differentiators.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.08}>
              <GlassCard className="p-6 h-full">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-sky-500/15 border border-sky-400/25 text-sky-300">
                  <v.icon size={19} />
                </span>
                <h3 className="mt-5 font-display text-base font-semibold text-white">{v.t}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.d}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* INCLUSIVE TALENT NETWORK */}
      <Section className="!pt-4">
        <Reveal>
          <SectionHeading
            eyebrow="Inclusive talent network"
            title="ONE CAMPAIGN. MULTIPLE POSSIBILITIES."
            subtitle="Whether your campaign needs a creator, videographer, photographer, editor or another specialised creative profile, Orbit is built to help you discover the people your work actually requires."
          />
        </Reveal>
        <div className="mt-12 grid gap-4 grid-cols-2 lg:grid-cols-5">
          {talent.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.07}>
              <GlassCard className="p-6 h-full flex flex-col items-start gap-4">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-cyan-500/12 border border-cyan-400/25 text-cyan-300">
                  <c.icon size={19} />
                </span>
                <span className="font-display font-semibold text-white text-sm sm:text-base">{c.t}</span>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* RISK-FREE BETA */}
      <Section className="!pt-4">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl glass p-8 sm:p-12 text-center">
            <div className="absolute inset-0 -z-10 opacity-80"
              style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(37,99,235,0.22), transparent 60%)" }} />
            <span className="pill mx-auto"><ShieldCheck size={13} /> Risk-free participation</span>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              TRY ORBIT WITHOUT THE RISK.
            </h2>
            <div className="mt-8 grid gap-4 md:grid-cols-2 text-left max-w-3xl mx-auto">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <p className="eyebrow">For Businesses</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Private beta access with 0% platform fee on deals. If Orbit doesn't work out for your campaign, you don't pay us anything.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <p className="eyebrow">For Creators</p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Join the private beta at no cost and get early access to opportunities as the network grows.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* ROLE SELECTION */}
      <Section id="enter-orbit" className="!pt-4" data-testid="enter-orbit-section">
        <Reveal>
          <SectionHeading center eyebrow="Request an invite" title="ENTER ORBIT" />
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Reveal>
            <GlassCard className="p-8 h-full flex flex-col">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-sky-500/15 border border-sky-400/25 text-sky-300">
                <Target size={22} />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">For Businesses</h3>
              <p className="mt-2 text-sm text-slate-400 flex-1">Launch campaigns. Discover relevant talent. Keep the process in your hands.</p>
              <Link to="/business-apply" data-testid="role-business" className="btn-primary mt-6 self-start">Request Business Access <ArrowRight size={18} /></Link>
            </GlassCard>
          </Reveal>
          <Reveal delay={0.1}>
            <GlassCard className="p-8 h-full flex flex-col">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-cyan-500/15 border border-cyan-400/25 text-cyan-300">
                <Users size={22} />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">For Creators</h3>
              <p className="mt-2 text-sm text-slate-400 flex-1">Get discovered for campaigns aligned with your skills and content.</p>
              <Link to="/creator-apply" data-testid="role-creator" className="btn-ghost mt-6 self-start">Join the Creator Waitlist <ArrowRight size={18} /></Link>
            </GlassCard>
          </Reveal>
        </div>
      </Section>

      {/* FINAL BRAND STATEMENT */}
      <Section className="!pt-4 !pb-28">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              THE WAY WORK GETS DISCOVERED IS CHANGING.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-300">
              Neo Orbit is building a more intelligent, direct and flexible layer between businesses and talent.
            </p>
            <div className="mt-8 flex justify-center">
              <InviteButton testid="final-cta-invite" />
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
